/**
 * SAMPLE DATA. None of this is measured, computed or fetched.
 *
 * The Video QA Studio v2 page is a showcase of the tracking wizard's review
 * surface. The R2 sample folder it plays from holds video and reference
 * frames only — no detections, no tracks, no calibration solve — so every
 * number, box and suspicion below is a fixture transcribed from a real
 * session of the wizard against sample1.mp4.
 *
 * IT LIVES IN ITS OWN FILE FOR ONE REASON: so that nothing on the page can
 * quietly start looking like a measurement. Anything imported from here is
 * sample data by construction, and the page says so on screen. The day this
 * surface gets a real backend, the import list is the exact work item.
 *
 * The copy is the wizard's own (priori_ben_tracking_wizard, web/src/guide.jsx
 * and panels.jsx), kept verbatim rather than paraphrased — showcasing a
 * different set of words would be showcasing something we do not have.
 */

/** The tab strip, in the wizard's own order and wording (api.js TABNAME). */
export const TABS = [
  'wizard', 'label', 'cleanup', 'play',
  'annotate', 'calibrate', 'players', 'stats',
  'jobs', 'videos',
] as const
export type Tab = (typeof TABS)[number]

export const TAB_LABEL: Record<Tab, string> = {
  wizard: 'Start here', label: 'Review', cleanup: 'Fix-ups', play: 'Play',
  annotate: 'Draw boxes', calibrate: 'Mark field', players: 'Shirt #s',
  stats: 'Scores', jobs: 'Jobs', videos: 'Videos',
}

/** Team colours, from the wizard's TEAMCOL. */
export const TEAM_COLOR: Record<string, string> = {
  TEN: '#ff9636', RUT: '#eb3c3c', REF: '#f5f531',
  GK: '#3cb4eb', SPEC: '#8a94a6',
}

export const RUN = {
  video: 'sample1.mp4',
  runId: 61,
  frames: 900,
  fps: 29.93,
  detections: 29220,
  tracks: 45,
  calibration: { points: 10, reprojErrM: 0.17, lengthM: 109.1, widthM: 66.1 },
  readyPercent: 46,
}

/** Boxes for the frame the stage opens on, as percentages of the video box so
 *  they scale with it. Transcribed from the wizard's frame 0. */
export type Box = { label: string; team: keyof typeof TEAM_COLOR; x: number; y: number; w: number; h: number }
export const BOXES: Box[] = [
  { label: 'GK-E', team: 'GK', x: 7.2, y: 30.5, w: 2.0, h: 7.0 },
  { label: 'TEN-E', team: 'TEN', x: 15.5, y: 35.5, w: 2.0, h: 7.5 },
  { label: 'RUT-B', team: 'RUT', x: 26.5, y: 37.5, w: 2.0, h: 7.5 },
  { label: 'RUT-F', team: 'RUT', x: 27.0, y: 41.0, w: 2.0, h: 7.5 },
  { label: 'REF-2', team: 'REF', x: 33.5, y: 29.5, w: 2.0, h: 7.0 },
  { label: 'RUT-J', team: 'RUT', x: 34.0, y: 33.5, w: 2.0, h: 7.0 },
  { label: 'RUT-D', team: 'RUT', x: 37.0, y: 46.5, w: 2.0, h: 7.5 },
  { label: 'RUT-H', team: 'RUT', x: 40.0, y: 35.0, w: 2.0, h: 7.5 },
  { label: 'RUT-C', team: 'RUT', x: 47.0, y: 45.5, w: 2.0, h: 7.5 },
  { label: 'RUT-G', team: 'RUT', x: 47.5, y: 33.0, w: 2.0, h: 7.0 },
  { label: 'TEN-G', team: 'TEN', x: 50.0, y: 32.5, w: 2.0, h: 7.0 },
  { label: 'RUT-I', team: 'RUT', x: 49.0, y: 37.5, w: 2.0, h: 7.5 },
  { label: 'RUT-E', team: 'RUT', x: 46.5, y: 40.5, w: 2.0, h: 7.0 },
  { label: 'TEN-D', team: 'TEN', x: 49.0, y: 40.5, w: 2.0, h: 7.0 },
  { label: 'RUT-A', team: 'RUT', x: 46.5, y: 53.0, w: 2.0, h: 7.5 },
  { label: 'TEN-B', team: 'TEN', x: 53.5, y: 45.5, w: 2.0, h: 7.5 },
  { label: 'TEN-J', team: 'TEN', x: 54.0, y: 33.0, w: 2.0, h: 7.0 },
  { label: 'REF-3', team: 'REF', x: 56.5, y: 34.5, w: 2.0, h: 7.0 },
  { label: 'TEN-F', team: 'TEN', x: 56.5, y: 37.5, w: 2.0, h: 7.0 },
  { label: 'GK-B', team: 'GK', x: 52.0, y: 29.5, w: 2.0, h: 7.0 },
  { label: 'TEN-GK', team: 'GK', x: 53.0, y: 31.5, w: 2.0, h: 6.5 },
  { label: 'GK-A', team: 'GK', x: 61.0, y: 29.5, w: 2.0, h: 7.0 },
  { label: 'TEN-H', team: 'TEN', x: 62.0, y: 32.5, w: 2.0, h: 7.0 },
  { label: 'TEN-C', team: 'TEN', x: 68.0, y: 34.5, w: 2.0, h: 7.5 },
  { label: 'TEN-A', team: 'TEN', x: 70.5, y: 39.5, w: 2.0, h: 7.0 },
  { label: 'TEN-I', team: 'TEN', x: 70.5, y: 43.5, w: 2.0, h: 7.5 },
]

/** The setup guide. Titles, durations and the step-5 body are the wizard's. */
export const STEPS = [
  { n: 1, title: 'Pick the video you want to work on', mins: '1 min', state: 'done' },
  { n: 2, title: 'Let the machine watch it once', mins: '≈2 min per 30 s of video', state: 'done' },
  { n: 3, title: 'Show it where the field is', mins: '5–10 min, once per venue', state: 'done' },
  { n: 4, title: 'Check its guesses, one player at a time', mins: '≈2 min per frame', state: 'done' },
  { n: 5, title: 'Get your first score', mins: '10 seconds', state: 'current' },
  { n: 6, title: 'Fix the things it is unsure about', mins: '5–15 min', state: 'todo' },
  { n: 7, title: 'Point out the two goalkeepers', mins: '2 min', state: 'todo' },
  { n: 8, title: 'Sweep out the people who are not playing', mins: '5 min', state: 'todo' },
  { n: 9, title: 'Give the players their shirt numbers', mins: '10 min', state: 'todo' },
  { n: 10, title: 'Re-run it with everything you just taught it', mins: '≈2 min per 30 s', state: 'todo' },
  { n: 11, title: 'Watch it back', mins: '2 min', state: 'todo' },
] as const

/** The Fix-ups list — the wizard's own two suspicion phrasings. */
export const SUSPICIONS = [
  { label: 'GK-B', frame: 3, why: 'the app boxed somebody here, but your labelling for this frame has nobody at this spot' },
  { label: 'GK-E', frame: 3, why: 'the app boxed somebody here, but your labelling for this frame has nobody at this spot' },
  { label: 'GK-B', frame: 112, why: 'you labelled this person a spectator, but the app is tracking them as taking part' },
  { label: 'GK-A', frame: 112, why: 'you labelled this person a spectator, but the app is tracking them as taking part' },
  { label: 'GK-E', frame: 112, why: 'the app boxed somebody here, but your labelling for this frame has nobody at this spot' },
  { label: 'GK-A', frame: 123, why: 'you labelled this person a spectator, but the app is tracking them as taking part' },
  { label: 'GK-B', frame: 123, why: 'the app boxed somebody here, but your labelling for this frame has nobody at this spot' },
]
export const SUSPICION_TOTAL = 143

/** Tracks, as the wizard's TracksCard lists them. */
export const TRACKS = [
  { label: 'TEN-A', team: 'TEN', conf: 1.0, offPct: 0, frames: '0–200' },
  { label: 'TEN-B', team: 'TEN', conf: 1.0, offPct: 54, frames: '0–899' },
  { label: 'TEN-C', team: 'TEN', conf: 0.99, offPct: 0, frames: '0–209' },
  { label: 'TEN-D', team: 'TEN', conf: 0.98, offPct: 0, frames: '0–278' },
  { label: 'TEN-E', team: 'TEN', conf: 0.89, offPct: 0, frames: '0–242' },
  { label: 'TEN-F', team: 'TEN', conf: 0.99, offPct: 0, frames: '0–899' },
  { label: 'TEN-G', team: 'TEN', conf: 1.0, offPct: 0, frames: '0–899' },
  { label: 'RUT-A', team: 'RUT', conf: 0.97, offPct: 2, frames: '0–899' },
  { label: 'RUT-B', team: 'RUT', conf: 0.95, offPct: 0, frames: '0–640' },
  { label: 'REF-2', team: 'REF', conf: 0.91, offPct: 11, frames: '0–899' },
]
export const TRACK_COUNTS = { participants: 40, rejected: 5 }

/** Ground-truth panel state (Review / Draw boxes). */
export const GROUND_TRUTH = {
  completeFrames: [3, 112, 123, 264, 327, 351, 370, 457, 458, 562, 602, 699, 766, 787, 789],
  partialFrames: 0,
  boxesThisFrame: 0,
  teams: ['TEN', 'RUT', 'REF', 'SPEC'],
}

/** Mark-field panel state. */
export const CALIBRATION = {
  boundaries: [
    { name: 'far', count: 12 }, { name: 'near', count: 4 },
    { name: 'left', count: 14 }, { name: 'right', count: 9 },
  ],
  landmarks: [
    { name: 'corner_far_left', placed: true, at: '-726,367' },
    { name: 'corner_far_right', placed: false, at: '(109.1, 0.0) m' },
    { name: 'corner_near_left', placed: false, at: '(0.0, 66.1) m' },
    { name: 'corner_near_right', placed: false, at: '(109.1, 66.1) m' },
    { name: 'halfway_far', placed: false, at: '(54.5, 0.0) m' },
    { name: 'halfway_near', placed: false, at: '(54.5, 66.1) m' },
  ],
}

/** Timeline registration bands, as fractions of the clip. */
export const REGISTRATION = [
  { kind: 'direct', from: 0, to: 4 },
  { kind: 'chained', from: 4, to: 12 },
  { kind: 'labelled', from: 12, to: 16 },
  { kind: 'chained', from: 16, to: 24 },
  { kind: 'direct', from: 24, to: 27 },
  { kind: 'labelled', from: 27, to: 34 },
  { kind: 'chained-back', from: 34, to: 44 },
  { kind: 'failed', from: 44, to: 47 },
  { kind: 'chained', from: 47, to: 100 },
] as const

export const REGISTRATION_COLOR: Record<string, string> = {
  direct: '#3ba55d', chained: '#2b5cab', 'chained-back': '#b4762b',
  failed: '#eb3c3c', labelled: '#57d98a',
}

export const JOBS = [
  { id: 61, kind: 'process', status: 'done', took: '4m 12s' },
  { id: 60, kind: 'evaluate', status: 'done', took: '9s' },
  { id: 59, kind: 'calibrate', status: 'done', took: '2s' },
  { id: 58, kind: 'process', status: 'failed', took: '1m 03s' },
]

export const SCORES = { measured: false }
